// Split out of src/lib/utils.js (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). utils.js re-exports
// everything here, so existing imports keep working; new code may import from here directly.

import { AGE_UP_THRESHOLD } from "./age.js";
import { ACTIVITY_KIND_STYLE, CARD_COLORS } from "./constants.js";
import { fmtNumber, fmtPence } from "./money.js";

// The child's "pop" (LB-163): the one colour the 8+ register lets loose beyond the hero —
// progress bars and the active tab. It is the hero colour itself, so a child who picks
// green gets a green tab and green bars. Falls back to the age theme's start colour.
export function kidPopColor(colorKey, kidTheme) {
  const c = CARD_COLORS[colorKey];
  if (c) return c.swatch;
  return (kidTheme || KID_THEMES.candy).gradientFrom;
}
export function cardGradientStyle(colorKey, kidTheme) {
  const c = CARD_COLORS[colorKey];
  if (c) return `linear-gradient(135deg, ${c.from}, ${c.to})`;
  const theme = kidTheme || KID_THEMES.candy;
  return `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`;
}
// Older versions of this app stored avatars as word-keys (e.g. "robot") referencing an
// icon component, not literal emoji. Map those old keys forward so imported backups
// from that era render correctly instead of showing the raw word.

// Splits at the unified AGE_UP_THRESHOLD (8) since Aug 2026 — previously this was the odd
// one out at 9 while the mascot name and arcade mode split at 7. Callers pass
// effectiveAge(child) (lib/age.js), not raw child.age, so the palette keeps up as children
// grow. Missing/non-numeric age keeps the candy theme here (mint needs a KNOWN older age).
export function getKidTheme(age) {
  return typeof age === "number" && age >= AGE_UP_THRESHOLD ? KID_THEMES.mint : KID_THEMES.candy;
}


// Maps a raw transaction row to the icon/colour/sign an ActivityRow needs — shared by the
// Kids tab's per-child "Recent activity" (KidDetail.jsx, was AdjustTab), the parent Home tab's
// household-wide activity list, and the kid Home tab's "Recent activity" — all render the
// same underlying state.transactions rows. Vault-transfer rows (tagged
// vaultTransfer at write time — see moveTokensToVault) keep their type-based colour/sign but get
// the vault icon swapped in, so a transfer still reads as a credit/debit while looking distinct
// from an ordinary earn or spend.
export function activityRowStyle(t) {
  // W4: the rate marker is informational — no sign, no number, muted styling.
  if (t.type === "rate_marker") {
    const m = ACTIVITY_KIND_STYLE.rate_marker;
    return { icon: m.icon, iconBgClass: m.iconBgClass, iconColorClass: m.iconColorClass, amount: "", amountColorClass: m.amountColorClass };
  }
  // LB-356 (Alan, 20 Sep): a gift of Oinks was recorded correctly (balance: "oinks") and then
  // drawn as a token credit, because nothing here read that field: coin icon, token green, a
  // bare "+10". Every list that shows transactions spreads this function's result into
  // ActivityRow, so the unit is decided here once rather than at each list. `unit` tells
  // ActivityRow to write the word after the number (the words live there, in ct(), so Content
  // Studio places them on a screen): a bare number beside token rows reads as tokens.
  if (t.balance === "oinks") {
    const o = ACTIVITY_KIND_STYLE.oinks;
    const oSign = t.type === "credit" ? "+" : "−";
    return { icon: o.icon, iconBgClass: o.iconBgClass, iconColorClass: o.iconColorClass, amount: `${oSign}${fmtNumber(t.amount)}`, unit: "oinks", amountColorClass: o.amountColorClass };
  }
  const base = t.type === "credit" ? ACTIVITY_KIND_STYLE.credit : t.type === "spend" ? ACTIVITY_KIND_STYLE.spend : ACTIVITY_KIND_STYLE.debit;
  const sign = t.type === "credit" ? "+" : "−";
  const amountValue = t.balance === "vault" ? fmtPence(t.amount) : fmtNumber(t.amount);
  return {
    icon: t.interest ? "ic-supersaver" : t.vaultTransfer ? "vault" : base.icon,
    iconBgClass: t.interest || t.vaultTransfer ? "bg-teal-50" : base.iconBgClass,
    iconColorClass: t.interest || t.vaultTransfer ? "text-teal-600" : base.iconColorClass,
    amount: `${sign}${amountValue}`,
    amountColorClass: t.interest ? "text-teal-600" : base.amountColorClass,
  };
}

export function wordSetFingerprint(words) {
  const key = (words || []).map((w) => w.word.trim().toLowerCase()).sort().join("|");
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(hash).toString(36);
}

// Kid redesign (docs/DESIGN_GUIDANCE.md §4/§6, Sep 2026): the theme is now the HERO THEME
// SLOT only — gradientFrom/To feed cardGradientStyle for the child's chosen hero colour and
// nothing else. Buttons are the house primary (navy), the active tab is the tinted pill in
// both modes, and the goal bar's token part is gold because that is what tokens are.
export const KID_THEMES = {
  candy: { primaryBtn: "bg-slate-900", activeTab: "kid-pop-bg text-white", progressFill: "kid-pop-fill", gradientFrom: "#2dd4bf", gradientTo: "#f472b6" },
  mint: { primaryBtn: "bg-slate-900", activeTab: "kid-pop-bg text-white", progressFill: "kid-pop-fill", gradientFrom: "#0d9488", gradientTo: "#134e4a" },
};
